import { useEffect, useRef, useState } from "react";
import type { HeroPublic, Me } from "@scrile/contracts";
import { useApi } from "../api/context";
import { describeError } from "../api/errors";
import { Icon } from "../components/Icon";
import { Link } from "../components/Link";
import { t } from "../i18n";
import { track } from "../lib/analytics";
import { takeCode } from "../lib/landing";
import { chargeLine, ctaLabel, planOffers, type PlanId } from "../lib/money";
import { go, href } from "../lib/router";

/**
 * Пейвол на месте поля ввода: бесплатные серии кончились (элемент ленты paywall) или
 * отправка получила 402. Выбор плана — как у аудио: годовой по умолчанию ценой в месяц,
 * месячный рядом, пробный период и сумма списания видны до оплаты. «Всё сразу» и
 * подробности — на полном пейволе по ссылке.
 */
export function PaywallDock({ hero, me, episode }: { hero: HeroPublic; me: Me; episode: number | null }) {
  const api = useApi();
  const { pricing } = hero;
  const offers = planOffers(pricing, me.trialEligible).filter((offer) => offer.plan !== "all_at_once");
  const [plan, setPlan] = useState<PlanId>(offers[0]?.plan ?? "yearly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = useRef(false);
  const selected = offers.find((offer) => offer.plan === plan) ?? offers[0] ?? null;

  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    track("paywall_shown", { episode: episode ?? undefined, screen: "chat" });
  }, [episode]);

  async function checkout() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    track("checkout_started", { screen: "chat" });
    try {
      const code = takeCode();
      const { url } = await api.checkout({ plan: selected.plan, ...(code ? { code } : {}) });
      go(url);
    } catch (checkoutError) {
      setError(describeError(checkoutError, "checkout"));
      setBusy(false);
    }
  }

  return (
    <section className="paywall-dock" aria-labelledby="paywall-dock-title">
      <div className="paywall-dock__head">
        <span className="paywall-dock__icon" aria-hidden="true">
          <Icon name="lock" size={18} />
        </span>
        <div>
          <h2 id="paywall-dock-title" className="paywall-dock__title">
            {episode !== null ? t("messenger.paywall.title", { n: episode }) : t("messenger.paywall.titleGeneric", { hero: hero.name })}
          </h2>
          <p className="paywall-dock__body">{t("messenger.paywall.body", { hero: hero.name })}</p>
        </div>
      </div>
      <fieldset className="paywall-dock__plans">
        <legend className="sr-only">{t("paywall.plans")}</legend>
        {offers.map((offer) => (
          <label key={offer.plan} className={`dock-plan${plan === offer.plan ? " dock-plan--selected" : ""}`}>
            <input type="radio" name="dock-plan" value={offer.plan} checked={plan === offer.plan} onChange={() => setPlan(offer.plan)} className="sr-only" />
            <span className="dock-plan__name">
              {t(`paywall.plan.${offer.plan}`)}
              {offer.badge && <span className="badge badge--accent">{offer.badge}</span>}
            </span>
            <span className="dock-plan__price">{offer.headline}</span>
          </label>
        ))}
      </fieldset>
      {selected && (
        <>
          <button type="button" className="btn btn--primary btn--block" onClick={() => void checkout()} disabled={busy}>
            {busy ? t("paywall.redirecting") : ctaLabel(selected, pricing)}
          </button>
          <p className="paywall-dock__charge">{chargeLine(selected, pricing)}</p>
        </>
      )}
      {error && (
        <p className="form__error" role="alert">
          {error}
        </p>
      )}
      <Link to={href.plans({ episode })} className="paywall-dock__more">
        {t("messenger.paywall.plans")}
      </Link>
    </section>
  );
}
